import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Droplets, Shield, Sprout } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: Leaf,
      title: 'Chemical-Free',
      description: '100% organic methods with zero synthetic pesticides or fertilizers.',
    },
    {
      icon: Droplets,
      title: 'Pure Water',
      description: 'Clean, filtered water systems ensuring optimal growing conditions.',
    },
    {
      icon: Shield,
      title: 'Hygiene First',
      description: 'Sterile environments and rigorous quality control protocols.',
    },
    {
      icon: Sprout,
      title: 'Organic Substrates',
      description: 'Natural growing mediums sourced from sustainable, renewable materials.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            About The Farm
          </h2>
          <div className="w-24 h-1 bg-forest-600 mx-auto mb-6"></div>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Woodsgrains is not just a farm—it's a biosphere. We cultivate sustainable ecosystems where nature and science work in harmony.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                className="glass rounded-2xl p-8 text-center hover:shadow-xl transition-shadow"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-forest-100 rounded-full mb-4">
                  <Icon className="w-8 h-8 text-forest-600" />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-stone-600">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-gradient-to-br from-forest-50 to-stone-50 rounded-3xl p-8 md:p-12 glass"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-stone-900 mb-6">
              Sustainable Ecosystems
            </h3>
            <p className="text-lg text-stone-700 leading-relaxed mb-6">
              Our approach goes beyond traditional farming. We create self-sustaining biospheres where each element supports the other. From the organic substrates that feed our mushrooms to the pure water systems that sustain them, every aspect of our operation is designed with ecological balance in mind.
            </p>
            <p className="text-lg text-stone-700 leading-relaxed">
              With scientific precision and deep respect for natural processes, we ensure that every harvest contributes to a healthier ecosystem—both within our farm and in the broader environment.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
